import React, { useState } from 'react';
import { Modal } from '../index';
import {
  Overlay,
  Container,
  Header,
  Title,
  CloseButton,
  Subtitle,
  Body,
  Content,
} from '../Terms/styled';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LanguageIcon from '@mui/icons-material/Language';

interface ModalDataDeletionProps {
  open: boolean;
  onClose: () => void;
}

type Language = 'en' | 'it';

const content = {
  en: {
    title: 'User Data Deletion',
    subtitle: 'How to request deletion of your data',
    info: "Kipify respects your right to delete personal data. Here's how you can remove your data from our platform.",
    optionsTitle: '📋 Data deletion options',
    connectorsTitle: 'Connector Removal',
    connectorsDesc: 'To delete data from a specific connector:',
    connectorsStep1: 'Go to Settings → Connectors',
    connectorsStep1Desc: "You'll find all your active connectors",
    connectorsStep2: 'Click "Remove" for the desired connector',
    connectorsStep2Desc:
      'This will delete all data associated with that connector',
    accountTitle: 'Complete Account Deletion',
    accountDesc: 'To completely delete your account and all data:',
    accountStep1: 'Send an email to support@kipify.io',
    accountStep1Desc: 'Subject: "Account deletion request"',
    accountStep2: 'Include your registered email address',
    accountStep2Desc: "We'll process the request within 30 days",
    whatGetsDeleted: '⚠️ What gets deleted',
    whatGetsDeletedItems: [
      'Connection data to external services (Shopify, Google Ads, Facebook, etc.)',
      'Personal metrics and reports',
      'Conversation and chat history',
      'Account settings and preferences',
    ],
    whatDoesntGetDeleted: "✅ What doesn't get deleted",
    whatDoesntGetDeletedItems: [
      'Aggregated anonymous data for general statistics',
      'Security and audit logs (retained for compliance)',
      'Billing data (retained for tax obligations)',
    ],
    footer: 'For more questions about privacy and data management, consult our',
    privacyLink: 'Privacy Policy',
  },
  it: {
    title: 'User Data Deletion',
    subtitle: 'Come richiedere la cancellazione dei tuoi dati',
    info: 'Kipify rispetta il tuo diritto alla cancellazione dei dati personali. Ecco come puoi eliminare i tuoi dati dalla nostra piattaforma.',
    optionsTitle: "📋 Opzioni per l'eliminazione dati",
    connectorsTitle: 'Rimozione Connettori',
    connectorsDesc: 'Per eliminare i dati di un connettore specifico:',
    connectorsStep1: 'Vai su Impostazioni → Connettori',
    connectorsStep1Desc: 'Troverai tutti i tuoi connettori attivi',
    connectorsStep2: 'Clicca su "Rimuovi" per il connettore desiderato',
    connectorsStep2Desc:
      'Questo eliminerà tutti i dati associati a quel connettore',
    accountTitle: 'Cancellazione Account Completa',
    accountDesc: 'Per eliminare completamente il tuo account e tutti i dati:',
    accountStep1: 'Invia una email a support@kipify.io',
    accountStep1Desc: 'Oggetto: "Richiesta cancellazione account"',
    accountStep2: 'Include il tuo indirizzo email registrato',
    accountStep2Desc: 'Processeremo la richiesta entro 30 giorni',
    whatGetsDeleted: '⚠️ What gets deleted',
    whatGetsDeletedItems: [
      'Dati di connessione ai servizi esterni (Shopify, Google Ads, Facebook, etc.)',
      'Metriche e report personalizzati',
      'Cronologia conversazioni e chat',
      'Impostazioni account e preferenze',
    ],
    whatDoesntGetDeleted: '✅ Cosa NON viene eliminato',
    whatDoesntGetDeletedItems: [
      'Dati aggregati anonimi per statistiche generali',
      'Log di sicurezza e audit (conservati per compliance)',
      'Dati di fatturazione (conservati per obblighi fiscali)',
    ],
    footer:
      'Per ulteriori domande sulla privacy e gestione dati, consulta la nostra',
    privacyLink: 'Privacy Policy',
  },
};

export const ModalDataDeletion: React.FC<ModalDataDeletionProps> = ({
  open,
  onClose,
}) => {
  const [language, setLanguage] = useState<Language>('en');
  const t = content[language];

  return (
    <Overlay style={{ display: open ? 'flex' : 'none' }}>
      <Container>
        <Header>
          <Title>{t.title}</Title>
          <Subtitle>{t.subtitle}</Subtitle>
          <CloseButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </Header>
        <Body>
          <Content>
            {/* Language Selector */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanguageIcon fontSize="small" />
                    Language
                  </Box>
                </InputLabel>
                <Select
                  value={language}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LanguageIcon fontSize="small" />
                      Language
                    </Box>
                  }
                  onChange={(e) => setLanguage(e.target.value as Language)}
                  sx={{
                    minHeight: 56,
                    '& .MuiSelect-select': {
                      padding: '16px 14px 8px 14px',
                      fontSize: '16px',
                    },
                  }}
                >
                  <MenuItem value="en">🇺🇸 English</MenuItem>
                  <MenuItem value="it">🇮🇹 Italiano</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">{t.info}</Typography>
            </Alert>

            <Typography variant="h6" sx={{ mb: 2, color: '#00bfa6' }}>
              {t.optionsTitle}
            </Typography>

            <List>
              <ListItem sx={{ alignItems: 'flex-start', mb: 2 }}>
                <ListItemIcon sx={{ mt: 0.5 }}>
                  <DeleteIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.connectorsTitle}
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t.connectorsDesc}
                      </Typography>
                      <List dense>
                        <ListItem sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <SettingsIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={t.connectorsStep1}
                            secondary={t.connectorsStep1Desc}
                          />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <DeleteIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={t.connectorsStep2}
                            secondary={t.connectorsStep2Desc}
                          />
                        </ListItem>
                      </List>
                    </Box>
                  }
                />
              </ListItem>

              <ListItem sx={{ alignItems: 'flex-start', mb: 2 }}>
                <ListItemIcon sx={{ mt: 0.5 }}>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.accountTitle}
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t.accountDesc}
                      </Typography>
                      <List dense>
                        <ListItem sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <LinkIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={t.accountStep1}
                            secondary={t.accountStep1Desc}
                          />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <DeleteIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={t.accountStep2}
                            secondary={t.accountStep2Desc}
                          />
                        </ListItem>
                      </List>
                    </Box>
                  }
                />
              </ListItem>
            </List>

            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#666' }}>
                {t.whatGetsDeleted}
              </Typography>
              {t.whatGetsDeletedItems.map((item, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  • {item}
                </Typography>
              ))}
            </Paper>

            <Paper sx={{ p: 2, bgcolor: '#e8f5e8', mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#2e7d32' }}>
                {t.whatDoesntGetDeleted}
              </Typography>
              {t.whatDoesntGetDeletedItems.map((item, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  • {item}
                </Typography>
              ))}
            </Paper>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t.footer}{' '}
                <a
                  href="/privacy"
                  style={{ color: '#00bfa6', textDecoration: 'underline' }}
                >
                  {t.privacyLink}
                </a>
              </Typography>
            </Box>
          </Content>
        </Body>
      </Container>
    </Overlay>
  );
};
